import { Typography } from '@mui/joy';
import { Warning } from '@phosphor-icons/react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { NumberHelper } from 'core/helpers';
import { useFormContext } from 'core/states';
import { IMargin } from 'pricing/types';
import { getLineCellValue } from 'quotes/components/QuotePartList/utils';
import {
  CostSection,
  PriceSection,
  PricingStateProvider,
  usePricingStateContext,
} from 'quotes/components/QuotePartUpsertLine';
import { PricingStrategyFormField } from 'quotes/elements';
import { PriceBasedOn } from 'quotes/enums';
import { QuotePartPricingHelper } from 'quotes/helpers';
import { ICustomerQuote, ICustomerQuotePart, IPartValue } from 'quotes/types';
import { parseSpaSelectionAvailable } from 'quotes/utils';
import { Color, Level } from 'theme/enums';

interface IInnerProps {
  quotePart: ICustomerQuotePart;
  quote?: ICustomerQuote;
  isPriceLocked: boolean;
}

const AlternatePricingSectionInner = ({ quotePart, quote, isPriceLocked }: IInnerProps) => {
  const { t } = useTranslation();

  const { watch, setValue, getValues } = useFormContext();

  const { isAlternateVersionEnabled, onChangePricingStrategy } = usePricingStateContext();

  const costValue = watch('cost');
  const replacementStandardCostValue = watch('replacementStandardCost');
  const priceBasedOnValue = watch('priceBasedOn') || PriceBasedOn.QUOTED;
  const corporateLocationIdValue = watch('corporateLocation')?.id;

  const activeCost =
    priceBasedOnValue === PriceBasedOn.REPLACEMENT
      ? Number(replacementStandardCostValue)
      : Number(costValue);

  const onPriceChange = useCallback(
    (price: number) => {
      setValue('margin', -1);

      if (price) {
        setValue('isPricingCustom', true);
        setValue('customMargin', getValues('pricingMargin'));
      }
    },
    [getValues, setValue]
  );

  const onCustomMarginChange = useCallback(
    (value: number) => {
      setValue('margin', -1);
      setValue('customMargin', value);
      setValue('isPricingCustom', true);
    },
    [setValue]
  );

  const corporateLocationHelperTexts = useMemo(() => {
    if (Number(corporateLocationIdValue) !== Number(quotePart?.corporateLocation?.id)) {
      return [
        <Typography
          key="shippingFieldsChangeWarning"
          color={Color.WARNING}
          level={Level.BODY_XS}
          startDecorator={<Warning weight="fill" />}
        >
          {t('messageShippingFieldsChangeWarning')}
        </Typography>,
      ];
    }

    return [];
  }, [corporateLocationIdValue, quotePart?.corporateLocation?.id, t]);

  // The vendor field rewrites part.supplier on a vendor change; the saved vendor until then.
  const isShipAndDebitVendor =
    (watch('part') as IPartValue | undefined)?.supplier?.isShipAndDebit ??
    quotePart?.supplier?.isShipAndDebit ??
    false;

  return (
    <>
      <CostSection
        quotePart={quotePart}
        quoteId={quotePart?.quoteId}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
        isShipAndDebitVendor={isShipAndDebitVendor}
        externalReplacementCost={quotePart?.externalReplacementCost}
        externalRepCostRegistrationNum={quotePart?.externalRepCostRegistrationNum}
        shipmentTypeRequestPayload={{ quotePart: { id: quotePart?.id }, filters: { deleted: 0 } }}
        corporateLocationHelperTexts={corporateLocationHelperTexts}
      />

      {isAlternateVersionEnabled && <PricingStrategyFormField onChange={onChangePricingStrategy} />}

      <PriceSection
        quote={quote}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
        disabledFields={isPriceLocked || !(activeCost > 0)}
        costFieldName="cost"
        marginFieldName="trueMargin"
        replacementCostFieldName="replacementStandardCost"
        priceBasedOnFieldName="priceBasedOn"
        onPriceChange={onPriceChange}
        onMarginChange={onCustomMarginChange}
      />
    </>
  );
};

interface IProps {
  quotePart: ICustomerQuotePart;
  quote?: ICustomerQuote;
  margins?: IMargin[];
  isPriceLocked: boolean;
}

export const AlternatePricingSection = ({ quotePart, quote, margins, isPriceLocked }: IProps) => {
  const { setValue, getValues } = useFormContext();

  const { costData: initialCostData, unifiedPartStatus: initialUnifiedPartStatus } = useMemo(
    () => QuotePartPricingHelper.mapQuotePartToCostData(quotePart),
    [quotePart]
  );

  const initialPriceData = useMemo(
    () => QuotePartPricingHelper.mapQuotePartToPriceData(quotePart),
    [quotePart]
  );

  const getCalculatePricesParams = useCallback(
    () => ({
      quoteId: quotePart?.quoteId,
      number: quotePart?.partArr?.number || '',
      manufacturerId: quotePart?.partArr?.manufacturerId,
      vendorNumber: quotePart?.partArr?.vendorNumber || '',
      quantity: quotePart?.quantity,
      uom: quotePart?.uom,
      lineReference: getLineCellValue(quotePart),
    }),
    [quotePart]
  );

  const onAfterProcessPrices = useCallback(
    ({ pricesUom }) => {
      const cost = pricesUom?.standardCost ?? pricesUom?.cost;
      setValue('cost', cost);

      const pricingMarginValue = parseFloat(getValues('pricingMargin'));

      if (!pricingMarginValue) {
        return;
      }

      const isCustomMargin = !margins?.find((m) =>
        NumberHelper.areEqual(m.percentage, pricingMarginValue, 4)
      );

      setValue('margin', isCustomMargin ? -1 : pricingMarginValue / 100);
      setValue('customMargin', pricingMarginValue);
    },
    [getValues, margins, setValue]
  );

  return (
    <PricingStateProvider
      isAlternateVersionEnabled
      priceDecimals={quotePart?.quotePriceDecimals}
      standardCostFieldName="cost"
      marginFieldName="trueMargin"
      initialCostData={initialCostData}
      initialPriceData={initialPriceData}
      initialUnifiedPartStatus={initialUnifiedPartStatus}
      initialSpaSelectionAvailable={parseSpaSelectionAvailable(
        quotePart?.externalSpaSelectionAvailable
      )}
      partNumber={quotePart?.partArr?.number}
      manufacturerId={quotePart?.partArr?.manufacturerId}
      quoteId={quotePart?.quoteId}
      quotePart={quotePart}
      getCalculatePricesParams={getCalculatePricesParams}
      onAfterProcessPrices={onAfterProcessPrices}
    >
      <AlternatePricingSectionInner
        quotePart={quotePart}
        quote={quote}
        isPriceLocked={isPriceLocked}
      />
    </PricingStateProvider>
  );
};
